<template>
  <div class="page admin-page">
    <div class="container">
      <div class="header">
        <h1><span>📚</span> 案例管理</h1>
        <div style="display: flex; align-items: center; gap: 20px;">
          <span class="user-info">👤 {{ currentUser.realName || '加载中...' }}</span>
          <button class="logout-btn" @click="goBack">我的主页</button>
        </div>
      </div>
      
      <div class="actions">
        <button class="btn btn-primary" @click="showCreateForm = !showCreateForm">
          {{ showCreateForm ? '取消创建' : '➕ 创建案例' }}
        </button>
        <button class="btn btn-success" @click="loadCases">
          🔄 刷新列表
        </button>
      </div>
      
      <div v-if="showCreateForm" class="form-container">
        <div class="form-item">
          <label>案例标题</label>
          <input v-model="newCase.title" placeholder="请输入案例标题">
        </div>
        <div class="form-item">
          <label>案例描述</label>
          <input v-model="newCase.description" placeholder="请输入案例描述">
        </div>
        <div style="grid-column: 1 / -1;">
          <button class="btn btn-primary" @click="createCase" :disabled="loading">
            {{ loading ? '创建中...' : '✅ 确认创建' }}
          </button>
        </div>
      </div>
      
      <div class="list-container">
        <div v-if="loading" class="loading">
          <div class="spinner"></div>
          <p style="margin-top: 12px;">加载中...</p>
        </div>
        
        <div v-else-if="cases.length === 0" class="empty-state">
          <div class="empty-icon">📭</div>
          <p>暂无案例，点击上方"创建案例"开始</p>
        </div>
        
        <div v-else>
          <div v-for="item in cases" :key="item.id" class="case-item">
            <div class="case-title">{{ item.title }}</div>
            <div class="case-desc">{{ item.description }}</div>
            <div class="case-meta">
              <span>ID: {{ item.id }}</span>
              <span>创建时间: {{ formatTime(item.createdTime) }}</span>
            </div>
            <div class="case-actions">
              <button class="btn btn-sm btn-primary" @click="toggleVideos(item.id)">
                {{ selectedCaseId === item.id ? '收起视频' : '管理视频' }}
              </button>
            </div>
            
            <!-- 视频管理区域 -->
            <div v-if="selectedCaseId === item.id" class="video-section">
              <div class="video-header">
                <h3>视频管理（{{ videos.length }} 个视频）</h3>
                <button class="btn btn-sm btn-success" @click="showUploadForm = !showUploadForm">
                  {{ showUploadForm ? '取消上传' : '➕ 上传视频' }}
                </button>
              </div>
              
              <div v-if="videoLoading" class="loading">
                <div class="spinner"></div>
                <p style="margin-top: 12px;">加载视频中...</p>
              </div>
              
              <div v-else-if="videos.length === 0" class="empty-state">
                <p>暂无视频，点击"上传视频"添加第一个视频</p>
              </div>
              
              <!-- 视频列表（表格形式） -->
              <div v-else class="video-table">
                <table>
                  <thead>
                    <tr>
                      <th>序号</th>
                      <th>视频标题</th>
                      <th>视频类型</th>
                      <th>播放顺序</th>
                      <th>关键词</th>
                      <th>标准答案</th>
                      <th>上传时间</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(video, index) in videos" :key="video.id">
                      <td>{{ index + 1 }}</td>
                      <td>{{ video.title }}</td>
                      <td>{{ getVideoTypeText(video.videoType) }}</td>
                      <td>{{ video.sortOrder }}</td>
                      <td>
                        <div v-if="video.keywords && video.keywords.length > 0">
                          <div v-for="(kw, i) in video.keywords" :key="i" style="margin-bottom: 4px;">
                            <span>{{ kw.keyword }}</span>
                            <span style="color: #667eea;">({{ kw.score }}分)</span>
                            <span v-if="kw.dimension" style="color: rgba(255,255,255,0.5); font-size: 12px;">{{ kw.dimension }}</span>
                          </div>
                        </div>
                        <span v-else>-</span>
                      </td>
                      <td>
                        <button v-if="video.videoType !== 0" class="btn btn-sm btn-outline" @click="openAnswerKeys(video)">
                          📝 管理
                          <span v-if="getAnswerKeyCount(video.id) > 0" class="badge">{{ getAnswerKeyCount(video.id) }}</span>
                        </button>
                        <span v-else>-</span>
                      </td>
                      <td>{{ formatTime(video.createdTime) }}</td>
                      <td>
                        <button class="btn btn-sm btn-primary" @click="playVideo(video)">播放</button>
                        <button v-if="video.videoType === 2" class="btn btn-sm btn-success" @click="openEditKeywords(video)">编辑关键词</button>
                        <button class="btn btn-sm btn-danger" @click="deleteVideo(video.id)">删除</button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <!-- 上传视频表单（可折叠） -->
              <div v-if="showUploadForm" class="upload-section">
                <h4>上传新视频</h4>
                <div class="form-item">
                  <label>视频标题</label>
                  <input v-model="newVideo.title" placeholder="请输入视频标题">
                </div>
                <div class="form-item">
                  <label>视频类型</label>
                  <select v-model="newVideo.videoType" @change="onVideoTypeChange">
                    <option value="0">系统使用教程</option>
                    <option value="1">情景介绍</option>
                    <option value="2">考试内容</option>
                    <option value="3">复盘</option>
                  </select>
                </div>
                <div class="form-item" v-if="newVideo.videoType === '2'">
                  <label>关键词设置</label>
                  <div v-for="(kw, idx) in newVideo.keywords" :key="idx" style="display: flex; gap: 10px; margin-bottom: 10px;">
                    <input v-model="kw.keyword" placeholder="关键词" style="flex: 2;">
                    <input v-model.number="kw.score" type="number" placeholder="分数" style="flex: 1;">
                    <select v-model="kw.dimension" style="flex: 1;">
                      <option value="">选择维度</option>
                      <option value="舆情监测">舆情监测</option>
                      <option value="危机应对">危机应对</option>
                      <option value="沟通表达">沟通表达</option>
                      <option value="分析判断">分析判断</option>
                      <option value="团队协作">团队协作</option>
                      <option value="创新思维">创新思维</option>
                    </select>
                    <button type="button" @click="newVideo.keywords.splice(idx, 1)" style="background: #ff6b6b;">删除</button>
                  </div>
                  <button type="button" @click="addKeyword" style="margin-top: 10px;">+ 添加关键词</button>
                </div>
                <div class="form-item">
                  <label>播放顺序</label>
                  <input v-model="newVideo.sortOrder" type="number" placeholder="请输入播放顺序">
                </div>
                <div class="form-item">
                  <label>选择视频文件</label>
                  <input type="file" @change="onFileChange" ref="fileInput">
                </div>
                <button class="btn btn-primary" @click="uploadVideo" :disabled="uploading || !selectedFile || !newVideo.title.trim()">
                  {{ uploading ? '上传中...' : '上传视频' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 编辑关键词弹窗 -->
    <div v-if="editKeywordDialog" class="dialog-overlay" @click.self="editKeywordDialog = false">
      <div class="dialog-content">
        <div class="dialog-header">
          <h3>编辑关键词 - {{ editingVideo?.title }}</h3>
          <button class="dialog-close" @click="editKeywordDialog = false">×</button>
        </div>
        <div class="dialog-body">
          <div v-for="(kw, idx) in editingKeywords" :key="idx" style="display: flex; gap: 10px; margin-bottom: 10px; align-items: center;">
            <input v-model="kw.keyword" placeholder="关键词" style="flex: 2; padding: 8px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 6px; color: #fff;">
            <input v-model.number="kw.score" type="number" placeholder="分数" style="flex: 1; padding: 8px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 6px; color: #fff;">
            <select v-model="kw.dimension" style="flex: 1; padding: 8px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 6px; color: #fff;">
              <option value="">选择维度</option>
              <option value="舆情监测">舆情监测</option>
              <option value="危机应对">危机应对</option>
              <option value="沟通表达">沟通表达</option>
              <option value="分析判断">分析判断</option>
              <option value="团队协作">团队协作</option>
              <option value="创新思维">创新思维</option>
            </select>
            <button @click="removeEditingKeyword(idx)" style="padding: 8px 12px; background: #ff6b6b; color: #fff; border: none; border-radius: 6px; cursor: pointer;">删除</button>
          </div>
          <button @click="addEditingKeyword" style="margin-top: 10px; padding: 8px 16px; background: rgba(102,126,234,0.2); color: #667eea; border: 1px solid rgba(102,126,234,0.3); border-radius: 6px; cursor: pointer;">+ 添加关键词</button>
        </div>
        <div class="dialog-footer">
          <button @click="editKeywordDialog = false" style="padding: 10px 20px; background: rgba(255,255,255,0.05); color: #fff; border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; cursor: pointer; margin-right: 10px;">取消</button>
          <button @click="saveKeywords" style="padding: 10px 20px; background: linear-gradient(135deg, #667eea, #764ba2); color: #fff; border: none; border-radius: 8px; cursor: pointer;">保存</button>
        </div>
      </div>
    </div>
    
    <!-- 标准答案管理弹窗 -->
    <div v-if="showAnswerKeyModal" class="dialog-overlay" @click.self="closeAnswerKeyModal">
      <div class="dialog-content" style="max-width: 700px;">
        <div class="dialog-header">
          <h3>📝 标准答案管理 - {{ currentVideo?.title }}</h3>
          <button class="dialog-close" @click="closeAnswerKeyModal">×</button>
        </div>
        <div class="dialog-body">
          <!-- 添加/编辑表单 -->
          <div v-if="showAnswerKeyForm" style="margin-bottom: 24px; padding: 20px; background: rgba(255,255,255,0.03); border-radius: 12px; border: 1px solid rgba(255,255,255,0.1);">
            <h4 style="margin: 0 0 16px 0; color: #667eea; font-size: 16px;">{{ editingAnswerKey ? '编辑标准答案' : '添加标准答案' }}</h4>
            <div style="margin-bottom: 12px;">
              <label style="display: block; font-size: 13px; color: rgba(255,255,255,0.7); margin-bottom: 6px;">答案内容</label>
              <textarea 
                v-model="answerKeyForm.content" 
                placeholder="请输入标准答案内容..." 
                style="width: 100%; padding: 12px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; color: #fff; font-size: 14px; outline: none; resize: vertical; min-height: 80px; font-family: inherit;"
                rows="4"
              ></textarea>
            </div>
            <div style="margin-bottom: 16px;">
              <label style="display: block; font-size: 13px; color: rgba(255,255,255,0.7); margin-bottom: 6px;">排序</label>
              <input 
                v-model="answerKeyForm.sortOrder" 
                type="number" 
                placeholder="0" 
                style="width: 100%; padding: 10px 14px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; color: #fff; font-size: 13px; outline: none;"
              >
            </div>
            <div style="display: flex; gap: 12px; justify-content: flex-end;">
              <button @click="showAnswerKeyForm = false" style="padding: 8px 20px; background: rgba(255,255,255,0.05); color: #fff; border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; cursor: pointer;">取消</button>
              <button @click="saveAnswerKey" style="padding: 8px 20px; background: linear-gradient(135deg, #667eea, #764ba2); color: #fff; border: none; border-radius: 8px; cursor: pointer;">确定</button>
            </div>
          </div>
          
          <!-- 答案列表 -->
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
            <h4 style="margin: 0; color: rgba(255,255,255,0.9); font-size: 16px;">标准答案列表</h4>
            <button @click="addAnswerKey" style="padding: 8px 16px; background: linear-gradient(135deg, #11998e, #38ef7d); color: #fff; border: none; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 600;">➕ 添加答案</button>
          </div>
          
          <div v-if="!answerKeys[currentVideo?.id] || answerKeys[currentVideo?.id].length === 0" style="text-align: center; padding: 40px; color: rgba(255,255,255,0.3);">
            <div style="font-size: 48px; margin-bottom: 12px;">📝</div>
            <p>暂无标准答案，点击上方按钮添加</p>
          </div>
          
          <div v-else style="display: flex; flex-direction: column; gap: 12px;">
            <div v-for="(ak, index) in answerKeys[currentVideo?.id]" :key="ak.id" style="padding: 16px; background: rgba(255,255,255,0.03); border-radius: 10px; border: 1px solid rgba(255,255,255,0.08); transition: all 0.3s;">
              <div style="display: flex; justify-content: space-between; align-items: flex-start;">
                <div style="flex: 1; display: flex; gap: 12px;">
                  <span style="color: #667eea; font-weight: 600; min-width: 24px;">{{ index + 1 }}.</span>
                  <span style="color: rgba(255,255,255,0.8); line-height: 1.6; flex: 1;">{{ ak.content }}</span>
                </div>
                <div style="display: flex; gap: 8px; margin-left: 12px;">
                  <button @click="editAnswerKey(ak)" style="width: 32px; height: 32px; border: none; background: rgba(255,255,255,0.05); border-radius: 6px; cursor: pointer; font-size: 14px; display: flex; align-items: center; justify-content: center; transition: all 0.3s;">✏️</button>
                  <button @click="deleteAnswerKey(ak)" style="width: 32px; height: 32px; border: none; background: rgba(255,82,82,0.1); border-radius: 6px; cursor: pointer; font-size: 14px; display: flex; align-items: center; justify-content: center; transition: all 0.3s;">🗑️</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'Cases',
  data() {
    return {
      currentUser: JSON.parse(localStorage.getItem('userInfo') || '{}'),
      cases: [],
      newCase: { title: '', description: '' },
      showCreateForm: false,
      loading: false,
      // 视频管理相关
      selectedCaseId: null,
      videos: [],
      videoLoading: false,
      newVideo: { title: '', videoType: '1', sortOrder: '0', keywords: [] },
      uploading: false,
      selectedFile: null,
      showUploadForm: false,
      editingVideo: null,
      editingKeywords: [],
      editKeywordDialog: false,
      // 标准答案管理
      currentVideo: null,
      answerKeys: {},
      showAnswerKeyModal: false,
      showAnswerKeyForm: false,
      editingAnswerKey: null,
      answerKeyForm: { content: '', sortOrder: 0 },
    }
  },
  mounted() {
    this.loadCases()
  },
  methods: {
    async loadCases() {
      this.loading = true
      try {
        const response = await axios.get('/api/cases')
        if (Array.isArray(response.data)) {
          this.cases = response.data
        } else if (response.data && response.data.code === 200) {
          this.cases = response.data.data || []
        }
      } catch (error) {
        console.error('加载案例失败:', error)
        alert('加载失败：' + (error.response?.data?.message || error.message))
      } finally {
        this.loading = false
      }
    },
    
    async createCase() {
      if (!this.newCase.title) {
        alert('请输入案例标题')
        return
      }
      
      this.loading = true
      try {
        const response = await axios.post('/api/cases', this.newCase)
        if (response.data && response.data.code === 200) {
          alert('创建成功！')
          this.newCase = { title: '', description: '' }
          this.showCreateForm = false
          await this.loadCases()
        }
      } catch (error) {
        console.error('创建案例失败:', error)
        alert('创建失败：' + (error.response?.data?.message || error.message))
      } finally {
        this.loading = false
      }
    },
    
    // 视频管理相关方法
    toggleVideos(caseId) {
      if (this.selectedCaseId === caseId) {
        this.selectedCaseId = null
        this.videos = []
      } else {
        this.selectedCaseId = caseId
        this.loadVideos(caseId)
      }
    },
    
    async loadVideos(caseId) {
      this.videoLoading = true
      try {
        const response = await axios.get(`/api/videos/case/${caseId}`)
        if (Array.isArray(response.data)) {
          this.videos = response.data
          for (let video of this.videos) {
            const kwRes = await axios.get(`/api/videos/${video.id}/keywords`)
            video.keywords = kwRes.data || []
          }
        }
      } catch (error) {
        console.error('加载视频失败:', error)
        alert('加载视频失败：' + (error.response?.data?.message || error.message))
      } finally {
        this.videoLoading = false
      }
    },
    
    onFileChange(event) {
      this.selectedFile = event.target.files[0]
    },
    
    onVideoTypeChange() {
      if (this.newVideo.videoType !== '2') {
        this.newVideo.keywords = []
      }
    },

    getVideoTypeText(type) {
      const types = { 0: '系统使用教程', 1: '情景介绍', 2: '考试内容', 3: '复盘' }
      return types[type] || '未知'
    },
    
    addKeyword() {
      this.newVideo.keywords.push({ keyword: '', score: 10, dimension: '' })
    },
    
    async uploadVideo() {
      if (!this.selectedFile || !this.newVideo.title.trim()) {
        alert('请选择视频文件并填写视频标题')
        return
      }
      
      this.uploading = true
      const formData = new FormData()
      formData.append('video', this.selectedFile)
      formData.append('caseId', this.selectedCaseId)
      formData.append('title', this.newVideo.title.trim())
      formData.append('videoType', this.newVideo.videoType)
      formData.append('sortOrder', this.newVideo.sortOrder)
      
      const keywordsStr = this.newVideo.keywords
        .filter(kw => kw.keyword.trim())
        .map(kw => `${kw.keyword.trim()}:${kw.score || 10}:${kw.dimension || ''}`)
        .join(',')
      formData.append('keywords', keywordsStr)
      
      try {
        const response = await axios.post('/api/videos/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        
        if (response.data) {
          alert('上传成功！')
          this.newVideo = { title: '', videoType: '1', sortOrder: '0', keywords: [] }
          this.selectedFile = null
          if (this.$refs.fileInput) this.$refs.fileInput.value = ''
          await this.loadVideos(this.selectedCaseId)
        }
      } catch (error) {
        console.error('上传视频失败:', error)
        alert('上传失败：' + (error.response?.data?.message || error.message))
      } finally {
        this.uploading = false
      }
    },
    
    playVideo(video) {
      window.open(video.url, '_blank')
    },
    
    async deleteVideo(videoId) {
      if (!confirm('确定要删除这个视频吗？')) return
      
      try {
        await axios.delete(`/api/videos/${videoId}`)
        alert('删除成功！')
        await this.loadVideos(this.selectedCaseId)
      } catch (error) {
        console.error('删除视频失败:', error)
        alert('删除失败：' + (error.response?.data?.message || error.message))
      }
    },
    
    // 标准答案管理
    getAnswerKeyCount(videoId) {
      return this.answerKeys[videoId] ? this.answerKeys[videoId].length : 0
    },
    
    async openAnswerKeys(video) {
      this.currentVideo = video
      this.showAnswerKeyModal = true
      await this.loadAnswerKeys(video.id)
    },
    
    async loadAnswerKeys(videoId) {
      try {
        const response = await axios.get(`/api/videos/${videoId}/answer-keys`)
        this.$set(this.answerKeys, videoId, response.data || [])
      } catch (error) {
        console.error('加载标准答案失败:', error)
        alert('加载标准答案失败: ' + (error.response?.data?.message || error.message))
      }
    },
    
    addAnswerKey() {
      this.editingAnswerKey = null
      this.answerKeyForm = { content: '', sortOrder: 0 }
      this.showAnswerKeyForm = true
    },
    
    editAnswerKey(answerKey) {
      this.editingAnswerKey = answerKey
      this.answerKeyForm = {
        content: answerKey.content,
        sortOrder: answerKey.sortOrder || 0
      }
      this.showAnswerKeyForm = true
    },
    
    async saveAnswerKey() {
      if (!this.answerKeyForm.content.trim()) {
        alert('请输入答案内容')
        return
      }
      
      try {
        if (this.editingAnswerKey) {
          await axios.put(`/api/videos/answer-keys/${this.editingAnswerKey.id}`, {
            content: this.answerKeyForm.content,
            sortOrder: this.answerKeyForm.sortOrder
          })
        } else {
          await axios.post(`/api/videos/${this.currentVideo.id}/answer-keys`, {
            content: this.answerKeyForm.content,
            sortOrder: this.answerKeyForm.sortOrder
          })
        }
        
        alert('保存成功！')
        this.showAnswerKeyForm = false
        await this.loadAnswerKeys(this.currentVideo.id)
      } catch (error) {
        alert('保存失败: ' + (error.response?.data?.message || error.message))
      }
    },
    
    async deleteAnswerKey(answerKey) {
      if (!confirm('确定要删除这个标准答案吗？')) return
      
      try {
        await axios.delete(`/api/videos/answer-keys/${answerKey.id}`)
        alert('删除成功！')
        await this.loadAnswerKeys(this.currentVideo.id)
      } catch (error) {
        alert('删除失败: ' + (error.response?.data?.message || error.message))
      }
    },
    
    closeAnswerKeyModal() {
      this.showAnswerKeyModal = false
      this.currentVideo = null
      this.showAnswerKeyForm = false
      this.editingAnswerKey = null
    },
    
    // 编辑关键词相关方法
    openEditKeywords(video) {
      this.editingVideo = video
      this.editingKeywords = JSON.parse(JSON.stringify(video.keywords || []))
      this.editKeywordDialog = true
    },
    
    addEditingKeyword() {
      this.editingKeywords.push({ keyword: '', score: 10, dimension: '' })
    },
    
    removeEditingKeyword(index) {
      this.editingKeywords.splice(index, 1)
    },
    
    async saveKeywords() {
      try {
        await axios.put(`/api/videos/${this.editingVideo.id}/keywords`, this.editingKeywords)
        alert('保存成功！')
        this.editKeywordDialog = false
        await this.loadVideos(this.selectedCaseId)
      } catch (error) {
        console.error('保存关键词失败:', error)
        alert('保存失败：' + (error.response?.data?.message || error.message))
      }
    },
    
    goBack() {
      if (this.$route.path !== '/home') {
        this.$router.push('/home')
      }
    },
    
    formatTime(time) {
      if (!time) return '未知时间'
      return new Date(time).toLocaleString()
    }
  }
}
</script>

<style scoped>
.page {
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
  background: linear-gradient(135deg, #0c0c1d 0%, #1a1a2e 100%);
  min-height: 100vh;
  width: 100vw;
  padding: 20px;
  color: #fff;
  overflow-x: hidden;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 24px 30px;
  background: rgba(255,255,255,0.03);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255,255,255,0.1);
  animation: slideDown 0.5s ease-out;
}

@keyframes slideDown {
  from { opacity:0; transform: translateY(-20px); }
  to { opacity:1; transform: translateY(0); }
}

.header h1 {
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 2px;
}

.header h1 span {
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.user-info {
  color: rgba(255,255,255,0.7);
  font-size: 14px;
}

.logout-btn {
  padding: 10px 24px;
  background: rgba(255,255,255,0.05);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.logout-btn:hover {
  background: rgba(255,255,255,0.1);
}

.actions {
  display: flex;
  gap: 15px;
  margin-bottom: 30px;
  padding: 20px;
  background: rgba(255,255,255,0.03);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255,255,255,0.1);
  animation: fadeIn 0.6s ease-out 0.2s both;
}

@keyframes fadeIn {
  from { opacity:0; }
  to { opacity:1; }
}

.btn {
  padding: 12px 28px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  letter-spacing: 0.5px;
  position: relative;
  overflow: hidden;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102,126,234,0.4);
}

.btn-success {
  background: linear-gradient(135deg, #11998e, #38ef7d);
  color: white;
}

.btn-success:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(56,239,125,0.4);
}

.btn-danger {
  background: linear-gradient(135deg, #ff6b6b, #ee5a6f);
  color: white;
}

.btn-danger:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(255,107,107,0.4);
}

.btn-outline {
  background: transparent;
  border: 1px solid rgba(255,255,255,0.2);
  color: #fff;
  padding: 6px 12px;
  font-size: 12px;
  border-radius: 6px;
}

.btn-outline:hover {
  background: rgba(255,255,255,0.05);
}

.badge {
  display: inline-block;
  padding: 2px 8px;
  background: rgba(102,126,234,0.3);
  color: #667eea;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  margin-left: 6px;
}

.btn-sm {
  padding: 6px 16px;
  font-size: 12px;
  border-radius: 6px;
}

.form-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 30px;
  padding: 24px;
  background: rgba(255,255,255,0.03);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255,255,255,0.1);
}

.form-item {
  margin-bottom: 16px;
}

.form-item label {
  display: block;
  font-size: 14px;
  color: rgba(255,255,255,0.7);
  margin-bottom: 8px;
  letter-spacing: 0.5px;
}

.form-item input, .form-item select {
  width: 100%;
  padding: 12px 16px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  transition: all 0.3s;
  outline: none;
}

.form-item input:focus, .form-item select:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102,126,234,0.2);
  background: rgba(102,126,234,0.05);
}

.list-container {
  padding: 24px;
  background: rgba(255,255,255,0.03);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255,255,255,0.1);
  animation: fadeIn 0.6s ease-out 0.4s both;
}

.case-item {
  padding: 24px;
  margin-bottom: 20px;
  background: rgba(255,255,255,0.02);
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.08);
  transition: all 0.3s;
}

.case-item:hover {
  background: rgba(102,126,234,0.05);
  border-color: rgba(102,126,234,0.3);
  transform: translateX(8px);
}

.case-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 10px;
}

.case-desc {
  color: rgba(255,255,255,0.5);
  font-size: 14px;
  line-height: 1.6;
}

.case-meta {
  display: flex;
  gap:20px;
  margin-top:12px;
  color: rgba(255,255,255,0.3);
  font-size:12px;
}

.empty-state {
  text-align: center;
  padding:80px 20px;
  color: rgba(255,255,255,0.3);
}

.empty-icon {
  font-size:64px;
  margin-bottom:20px;
}

.loading {
  text-align: center;
  padding:40px;
}

.spinner {
  display: inline-block;
  width:24px;
  height:24px;
  border:3px solid rgba(102,126,234,0.3);
  border-top-color:#667eea;
  border-radius:50%;
  animation: spin 0.8s linear infinite;
}

.video-section {
  margin-top: 20px;
  padding: 20px;
  background: rgba(255,255,255,0.02);
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.1);
}

.video-table {
  overflow-x: auto;
}

.video-table table {
  width: 100%;
  border-collapse: collapse;
}

.video-table th {
  text-align: left;
  padding: 12px;
  background: rgba(102,126,234,0.1);
  color: #667eea;
  font-weight: 600;
  font-size: 13px;
  border-bottom: 2px solid rgba(102,126,234,0.2);
}

.video-table td {
  padding: 12px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
  font-size: 13px;
}

.video-table tr:hover {
  background: rgba(102,126,234,0.05);
}

.upload-section {
  margin-top: 20px;
  padding: 20px;
  background: rgba(255,255,255,0.02);
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.1);
}

.upload-section .form-item {
  margin-bottom: 12px;
}

.upload-section label {
  display: block;
  font-size: 13px;
  color: rgba(255,255,255,0.7);
  margin-bottom: 6px;
}

.upload-section input,
.upload-section select {
  width: 100%;
  padding: 10px 14px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  transition: all 0.3s;
  outline: none;
}

.case-actions {
  display: flex;
  gap: 10px;
  margin-top: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 弹窗样式 */
.dialog-overlay {
  position: fixed;
  top:0;
  left:0;
  width: 100vw;
  height: 100vh;
  background: rgba(0,0,0,0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.dialog-content {
  background: linear-gradient(135deg, #1a1a2e 0%, #2d2d44 100%);
  border-radius: 16px;
  border: 1px solid rgba(255,255,255,0.1);
  padding: 0;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
}

.dialog-header h3 {
  font-size: 20px;
  color: #667eea;
  font-weight: 600;
}

.dialog-close {
  background: none;
  border: none;
  color: rgba(255,255,255,0.5);
  font-size: 24px;
  cursor: pointer;
  padding:0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dialog-body {
  padding: 24px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  padding: 20px 24px;
  border-top: 1px solid rgba(255,255,255,0.1);
}
</style>
